import {
  Badge,
  Button,
  cn,
  confirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Label,
  Loader,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  CreditCard,
  DollarSign,
  MapPin,
  Move,
  Phone,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ShoppingBag,
  Table,
  User,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import { formatDateTimeShort } from "@/shared/lib/date-time";
import { ApiUserError } from "@/shared/lib/fetcher";
import type { PaymentInput } from "../../domain/types";
import { posApi } from "../api/client";
import {
  posKeys,
  useBranchTaxSettings,
  useCompleteOrder,
  useMoveOrderItems,
  useOrderDetail,
  useOrderPayments,
  useOrderRefunds,
  useOpenShift,
  usePaymentMethods,
  useServiceChargePresets,
  useUpdateOrderTable,
  useVoidOrder,
  useVoidOrderItem,
} from "../api/queries";
import type { CartItem } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import {
  buildMemberReceiptSnapshotForLocalCompleteWithTimeout,
} from "../lib/member-receipt";
import { paymentUiLog } from "../lib/payment-ui-log";
import { scheduleDeferredPrint, yieldToMain } from "../lib/print-html";
import {
  clearCustomerDisplayOrder,
  cartSnapshotsToDisplayCartItems,
  setCustomerDisplayOrder,
} from "../lib/customer-display-order";
import { clearCustomerDisplayCustomer } from "../lib/customer-display-customer";
import { clearCustomerDisplayTempCart } from "../lib/customer-display-temp-cart";
import {
  completeLocalOrder,
  getLocalOrder,
  listLocalOrderOperations,
  listLocalOrdersForShift,
  markLocalOrderSyncStatus,
  mergeLocalOrders,
  moveLocalOrderTable,
  OFFLINE_SYNC_CHANGED_EVENT,
  refundLocalOrder,
  replaceLocalOrderCustomerMapping,
  retryLocalCustomerDraftSync,
  updateLocalOrder,
  updateLocalOrderCustomerSnapshot,
  voidLocalOrder,
} from "../lib/offline-sync/db";
import {
  getCustomerSourceColor,
  getCustomerSourceLabel,
  getCustomerSyncStatusColor,
  getCustomerSyncStatusLabel,
  hasDiscountBreakdown,
  isLocalOrderCouponConflict,
} from "../lib/offline-sync/local-first-display-helpers";
import {
  clearLocalOrderReward,
  localOrderHasReward,
} from "../lib/offline-sync/clear-local-order-reward";
import { addProvisionalLoyaltyRedemption } from "../lib/offline-sync/loyalty-provisional";
import { mergeLocalPricingSnapshot } from "../lib/offline-sync/local-pricing-snapshot";
import {
  requestAutoSyncAfterAdjustment,
  requestAutoSyncAfterComplete,
  runAutoSyncNow,
  toAutoSyncScope,
} from "../lib/offline-sync/auto-sync";
import { buildSyncPayloadFromLocalOrder } from "../lib/offline-sync/sync-builder";
import type {
  LocalOrderCustomerSnapshot,
  LocalOrderOperationRecord,
  LocalOrderRecord,
} from "../lib/offline-sync/types";
import {
  printKitchenOnly,
  printLocalCancelKitchenNote,
  printLocalKitchenOnly,
  printLocalMergeOrderNote,
  printLocalMoveTableNote,
  printLocalProformaOnly,
  printLocalReceiptOnly,
  printLocalSupplementaryBillAndKitchen,
  printProformaOnly,
  printReceiptOnly,
} from "../lib/print-order-bill";
import { AddItemsDialog } from "../ui/AddItemsDialog";
import { LocalOrderPricingPanel } from "../ui/LocalOrderPricingPanel";
import { OrderSelectorDialog } from "../ui/OrderSelectorDialog";
import {
  PaymentDialog,
  type PaymentDialogCompletePricing,
  type PaymentDialogCouponApplication,
  type PaymentDialogCustomerSelection,
} from "../ui/PaymentDialog";
import { RefundDialog } from "../ui/RefundDialog";
import { RefundList } from "../ui/RefundList";
import { TableSelectorDialog } from "../ui/TableSelectorDialog";
import {
  OrderDetailActionGroup,
  OrderDetailActions,
} from "../ui/order-detail/OrderDetailActions";
import { OrderDetailHeader } from "../ui/order-detail/OrderDetailHeader";
import {
  OrderDetailItemRow,
  OrderDetailItemsList,
} from "../ui/order-detail/OrderDetailItemRow";
import { OrderDetailLayout } from "../ui/order-detail/OrderDetailLayout";
import { OrderDetailMetaGrid } from "../ui/order-detail/OrderDetailMetaGrid";
import { OrderDetailSection } from "../ui/order-detail/OrderDetailSection";
import { OrderDetailSummary } from "../ui/order-detail/OrderDetailSummary";
import {
  getOrderStatusBadgeClass,
  getOrderStatusLabel,
  getOrderTypeLabel,
  getSyncStatusBadgeClass,
  getSyncStatusLabel,
} from "../ui/order-detail/helpers";
import { VoidOrderDialog } from "../ui/VoidOrderDialog";

const getLocalDerivedStatus = (order: LocalOrderRecord): string => {
  const refundTotal = order.refundTotal ?? 0;
  if (refundTotal >= order.totals.grandTotal && order.totals.grandTotal > 0) {
    return "REFUNDED";
  }
  if (refundTotal > 0) return "PARTIAL_REFUNDED";
  return order.status;
};

const getOperationLabel = (type: LocalOrderOperationRecord["type"]) => {
  switch (type) {
    case "create_order":
      return "ສ້າງ order";
    case "update_order":
      return "ແກ້ໄຂ order";
    case "complete_order":
      return "ຈ່າຍເງິນ";
    case "void_order":
      return "ຍົກເລີກ order";
    case "refund_order":
      return "ຄືນເງິນ";
    case "move_table":
      return "ຍ້າຍໂຕະ";
    case "merge_orders":
      return "ລວມ order";
    case "sync_marked":
      return "sync state";
    case "manual_resolved":
      return "manual resolve";
    default:
      return type;
  }
};

const getOperationSyncColor = (
  status?: LocalOrderOperationRecord["syncStatus"],
) => {
  switch (status) {
    case "queued":
      return "border-amber-300 bg-amber-50 text-amber-800";
    case "syncing":
      return "border-blue-300 bg-blue-50 text-blue-800";
    case "synced":
      return "border-green-300 bg-green-50 text-green-800";
    case "failed":
    case "needs_review":
      return "border-red-300 bg-red-50 text-red-800";
    default:
      return "border-muted bg-muted/40 text-muted-foreground";
  }
};

function readOperationPayload(
  operation: LocalOrderOperationRecord,
): Record<string, unknown> {
  return operation.payload &&
    typeof operation.payload === "object" &&
    !Array.isArray(operation.payload)
    ? (operation.payload as Record<string, unknown>)
    : {};
}

function canReprintAdjustmentNote(type: LocalOrderOperationRecord["type"]) {
  return ["void_order", "move_table", "merge_orders"].includes(type);
}

function buildCustomerSnapshotFromPaymentSelection(
  customer: PaymentDialogCustomerSelection | undefined,
): LocalOrderCustomerSnapshot | undefined {
  if (!customer) return undefined;
  if (customer.customerSnapshot) return customer.customerSnapshot;
  const capturedAt = customer.capturedAt ?? new Date().toISOString();
  return {
    customerId: customer.id || undefined,
    localCustomerId: customer.localCustomerId,
    phone: customer.phone ?? undefined,
    firstName: customer.firstName ?? undefined,
    lastName: customer.lastName ?? undefined,
    source: customer.source ?? "server",
    capturedAt,
  };
}

function calculateItemTax(
  lineSubtotal: number,
  taxRate: number,
  taxIncluded: boolean,
): number {
  if (!taxIncluded || taxRate === 0) return 0;
  return Math.round(lineSubtotal * (taxRate / (100 + taxRate)) * 100) / 100;
}

function calculatePriceExcludingTax(
  inclusivePrice: number,
  taxRate: number,
  taxIncluded: boolean,
): number {
  if (!taxIncluded || taxRate === 0) return inclusivePrice;
  return Math.round((inclusivePrice / (1 + taxRate / 100)) * 100) / 100;
}

function getLocalOrderTypeValue(
  order: Pick<LocalOrderRecord, "isDelivery" | "selection">,
): "DINE_IN" | "TAKEAWAY" | "DELIVERY" {
  if (order.isDelivery) return "DELIVERY";
  if (order.selection.tableId) return "DINE_IN";
  return "TAKEAWAY";
}

export function OrderDetailPage() {
  const { orderId } = useParams({ from: "/app/pos/orders/$orderId" });
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? null;
  const { data: openShift } = useOpenShift(branchId, deviceId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const goBack = useRouterBack();
  const isLocalOrderId = orderId.startsWith("local_order");
  const serverOrderId = isLocalOrderId ? null : orderId;

  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isAddItemsDialogOpen, setIsAddItemsDialogOpen] = useState(false);
  const [localOrder, setLocalOrder] = useState<LocalOrderRecord | null>(null);
  const [localOrderOperations, setLocalOrderOperations] = useState<
    LocalOrderOperationRecord[]
  >([]);
  const [isLocalOrderLoading, setIsLocalOrderLoading] =
    useState(isLocalOrderId);
  const [isCompletingLocalOrder, setIsCompletingLocalOrder] = useState(false);
  const [isRemovingLocalReward, setIsRemovingLocalReward] = useState(false);
  const [isVoidingLocalOrder, setIsVoidingLocalOrder] = useState(false);
  const [isRefundingLocalOrder, setIsRefundingLocalOrder] = useState(false);
  const [localRefundReason, setLocalRefundReason] = useState("");
  const [localRefundMethodId, setLocalRefundMethodId] = useState("");
  const [isLocalMergeDialogOpen, setIsLocalMergeDialogOpen] = useState(false);
  const [localMergeCandidates, setLocalMergeCandidates] = useState<
    LocalOrderRecord[]
  >([]);
  const [localMergeSearch, setLocalMergeSearch] = useState("");
  const [isMergingLocalOrder, setIsMergingLocalOrder] = useState(false);
  const [isReplaceCustomerDialogOpen, setIsReplaceCustomerDialogOpen] =
    useState(false);
  const [replaceCustomerPhone, setReplaceCustomerPhone] = useState("");
  const [replaceCustomerFirstName, setReplaceCustomerFirstName] = useState("");
  const [replaceCustomerLastName, setReplaceCustomerLastName] = useState("");
  const [replaceCustomerId, setReplaceCustomerId] = useState("");
  const [isReplacingCustomer, setIsReplacingCustomer] = useState(false);
  const [voidLineItemId, setVoidLineItemId] = useState<string | null>(null);
  const [voidLineReason, setVoidLineReason] = useState("");

  const { data: orderData, isLoading } = useOrderDetail(
    serverOrderId,
    branchId,
  );
  const { data: paymentMethods = [] } = usePaymentMethods(branchId);
  const { data: taxSettings } = useBranchTaxSettings(branchId);
  const { data: serviceChargePresets = [] } = useServiceChargePresets(branchId);
  const { data: orderPayments = [], isLoading: isOrderPaymentsLoading } =
    useOrderPayments(serverOrderId, branchId);
  const { data: refunds = [] } = useOrderRefunds(serverOrderId, branchId);
  const updateTableMutation = useUpdateOrderTable();
  const moveItemsMutation = useMoveOrderItems();
  const completeOrderMutation = useCompleteOrder();
  const voidOrderMutation = useVoidOrder();
  const voidOrderItemMutation = useVoidOrderItem();
  const paymentMethodNameById = new Map(
    paymentMethods.map((method) => [method.id, method.displayName]),
  );

  useEffect(() => {
    return () => clearCustomerDisplayOrder();
  }, []);

  const localOrderInitialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!isLocalOrderId) return;

    let cancelled = false;
    localOrderInitialLoadDoneRef.current = false;

    const loadLocalOrder = async () => {
      // ຄົ້ນຫາລູກຄ້າ / ອັບເດດ cache ຍິງ OFFLINE_SYNC_CHANGED → ບໍ່ຄວນເປີດ Loader ທັງໜ້າ
      // (unmount PaymentDialog ແລ້ວເປີດໃໝ່)
      const silentRefresh = localOrderInitialLoadDoneRef.current;
      if (!silentRefresh) {
        setIsLocalOrderLoading(true);
      }

      const [record, operations] = await Promise.all([
        getLocalOrder(orderId),
        listLocalOrderOperations(orderId),
      ]);
      if (!cancelled) {
        setLocalOrder(record ?? null);
        setLocalOrderOperations(operations);
        localOrderInitialLoadDoneRef.current = true;
        if (!silentRefresh) {
          setIsLocalOrderLoading(false);
        }
      }
    };

    void loadLocalOrder();
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadLocalOrder);
    return () => {
      cancelled = true;
      localOrderInitialLoadDoneRef.current = false;
      window.removeEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadLocalOrder);
    };
  }, [isLocalOrderId, orderId]);

  useEffect(() => {
    if (!isLocalMergeDialogOpen || !localOrder) return;

    let cancelled = false;
    const loadCandidates = async () => {
      const records = await listLocalOrdersForShift(localOrder.shiftId);
      if (cancelled) return;
      setLocalMergeCandidates(
        records
          .filter((record) => record.clientOrderId !== localOrder.clientOrderId)
          .filter((record) => record.branchId === localOrder.branchId)
          .filter((record) => record.status === "OPEN")
          .filter(
            (record) =>
              !["syncing", "failed", "needs_review"].includes(
                record.syncStatus,
              ),
          )
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      );
    };

    void loadCandidates();
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadCandidates);
    return () => {
      cancelled = true;
      window.removeEventListener(OFFLINE_SYNC_CHANGED_EVENT, loadCandidates);
    };
  }, [isLocalMergeDialogOpen, localOrder]);

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-4xl p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <X className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const calculateLocalTotals = (
    cartItems: LocalOrderRecord["cartItems"],
    orderForType: LocalOrderRecord,
    discountTotal: number,
  ) => {
    const orderType = getLocalOrderTypeValue(orderForType);
    const grossTotal = cartItems.reduce(
      (sum, item) => sum + item.totalPrice,
      0,
    );
    const effectiveTaxSettings = taxSettings ?? {
      taxRate: 0,
      taxIncluded: false,
    };
    const subtotal = effectiveTaxSettings.taxIncluded
      ? cartItems.reduce(
          (sum, item) =>
            sum +
            calculatePriceExcludingTax(
              item.totalPrice,
              effectiveTaxSettings.taxRate,
              effectiveTaxSettings.taxIncluded,
            ),
          0,
        )
      : grossTotal;
    const taxTotal = effectiveTaxSettings.taxIncluded
      ? cartItems.reduce(
          (sum, item) =>
            sum +
            calculateItemTax(
              item.totalPrice,
              effectiveTaxSettings.taxRate,
              effectiveTaxSettings.taxIncluded,
            ),
          0,
        )
      : 0;
    const serviceChargeTotal = serviceChargePresets.reduce((sum, preset) => {
      const metadata = preset.metadata as
        | { orderTypes?: ("DINE_IN" | "TAKEAWAY" | "DELIVERY")[] }
        | null
        | undefined;
      if (
        metadata?.orderTypes?.length &&
        !metadata.orderTypes.includes(orderType)
      ) {
        return sum;
      }
      const calculationMode = preset.calculationMode?.toLowerCase();
      if (calculationMode === "percentage") {
        return sum + subtotal * (Number(preset.rate ?? 0) / 100);
      }
      if (calculationMode === "fixed") {
        return sum + Number(preset.amount ?? 0);
      }
      return sum;
    }, 0);

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountTotal,
      taxTotal: Math.round(taxTotal * 100) / 100,
      serviceChargeTotal: Math.round(serviceChargeTotal * 100) / 100,
      grandTotal:
        Math.round(
          (subtotal + serviceChargeTotal + taxTotal - discountTotal) * 100,
        ) / 100,
    };
  };

  const handleCompleteLocalOrder = async (
    payments: PaymentInput[],
    voucherCodes?: string[],
    paymentCouponCode?: string,
    couponOrderItemIds?: string[],
    paymentCouponApplications?: PaymentDialogCouponApplication[],
    paymentCustomerId?: string,
    paymentCustomerSelection?: PaymentDialogCustomerSelection,
    _rewardId?: string,
    paymentPricing?: PaymentDialogCompletePricing,
  ) => {
    if (!localOrder) return;

    setIsCompletingLocalOrder(true);
    setIsPaymentDialogOpen(false);
    paymentUiLog.info("local order complete start", {
      clientOrderId: localOrder.clientOrderId,
      branchId: localOrder.branchId,
      paymentCount: payments.length,
    });
    try {
      const customerSnapshot = buildCustomerSnapshotFromPaymentSelection(
        paymentCustomerSelection,
      );
      if (customerSnapshot || paymentCustomerId) {
        await updateLocalOrderCustomerSnapshot(localOrder.clientOrderId, {
          customerId: paymentCustomerId || undefined,
          customerSnapshot,
          customerSyncStatus:
            customerSnapshot?.source === "local_draft" ? "queued" : undefined,
        });
      }

      const baseTotals = calculateLocalTotals(
        localOrder.cartItems,
        localOrder,
        0,
      );
      const pricingSnapshot = mergeLocalPricingSnapshot({
        base: {
          subtotal: baseTotals.subtotal,
          taxTotal: baseTotals.taxTotal,
          serviceChargeTotal: baseTotals.serviceChargeTotal,
        },
        existingCouponSnapshots: localOrder.couponSnapshots,
        paymentCouponSnapshots: paymentPricing?.paymentCouponSnapshots,
        paymentVoucherSnapshots: paymentPricing?.paymentVoucherSnapshots,
        rewardSnapshot: paymentPricing?.rewardSnapshot,
      });

      const effectiveCustomerIdForReward =
        paymentCustomerId ||
        localOrder.customerId ||
        customerSnapshot?.customerId ||
        localOrder.customerSnapshot?.customerId;

      await yieldToMain();
      const memberReceiptSnapshot =
        await buildMemberReceiptSnapshotForLocalCompleteWithTimeout({
          branchId: localOrder.branchId,
          customerId: effectiveCustomerIdForReward,
          customerSnapshot:
            customerSnapshot ?? localOrder.customerSnapshot,
          cartItems: localOrder.cartItems,
          loyaltyPointsRedeemed:
            pricingSnapshot.rewardSnapshot?.pointsCost,
          rewardSnapshot: pricingSnapshot.rewardSnapshot,
        });

      const paidOrder = await completeLocalOrder(localOrder.clientOrderId, {
        payments,
        customerId: paymentCustomerId || undefined,
        customerSnapshot,
        customerSyncStatus:
          customerSnapshot?.source === "local_draft" ? "queued" : undefined,
        memberReceiptSnapshot,
        voucherCodes,
        paymentCouponCode,
        couponOrderItemIds,
        paymentCouponApplications,
        couponSnapshots: pricingSnapshot.couponSnapshots,
        voucherSnapshots: pricingSnapshot.voucherSnapshots,
        rewardSnapshot: pricingSnapshot.rewardSnapshot,
        rewardId:
          paymentPricing?.rewardId ?? pricingSnapshot.rewardSnapshot?.rewardId,
        discountBreakdown: pricingSnapshot.discountBreakdown,
        pricingSource: pricingSnapshot.pricingSource,
        totals: pricingSnapshot.totals,
        completedShiftId: openShift?.id,
        completedAt: new Date().toISOString(),
      });
      if (
        pricingSnapshot.rewardSnapshot &&
        effectiveCustomerIdForReward &&
        pricingSnapshot.rewardSnapshot.pointsCost > 0
      ) {
        addProvisionalLoyaltyRedemption({
          branchId: localOrder.branchId,
          customerId: effectiveCustomerIdForReward,
          clientOrderId: localOrder.clientOrderId,
          points: pricingSnapshot.rewardSnapshot.pointsCost,
        });
      }
      const nextOrder = paidOrder ?? localOrder;
      setLocalOrder(nextOrder);
      setIsCompletingLocalOrder(false);
      toast.success("ຈ່າຍເງິນສຳເລັດແລ້ວ — ລໍຖ້າ sync");

      paymentUiLog.info("local order complete success", {
        clientOrderId: nextOrder.clientOrderId,
      });

      requestAutoSyncAfterComplete(toAutoSyncScope(nextOrder));
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(nextOrder.branchId),
        }),
      ]).catch((invalidateErr) => {
        paymentUiLog.error("local order invalidate failed", {
          errorMessage:
            invalidateErr instanceof Error
              ? invalidateErr.message
              : String(invalidateErr),
        });
      });

      scheduleDeferredPrint(() => {
        return printLocalReceiptOnly(nextOrder).catch((printErr) => {
          paymentUiLog.error("local order print failed", {
            clientOrderId: nextOrder.clientOrderId,
            errorMessage:
              printErr instanceof Error ? printErr.message : String(printErr),
          });
          toast.error(
            printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
          );
        });
      });
    } catch (error) {
      paymentUiLog.error("local order complete failed", {
        clientOrderId: localOrder.clientOrderId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຈ່າຍເງິນ",
      );
    } finally {
      setIsCompletingLocalOrder(false);
    }
  };

  const handleAddItemsToLocalOrder = async (newItems: CartItem[]) => {
    if (!localOrder) return;
    if (localOrder.status !== "OPEN") {
      throw new Error("Local order ນີ້ບໍ່ສາມາດເພີ່ມລາຍການໄດ້ແລ້ວ");
    }

    const nextCartItems = [...localOrder.cartItems, ...newItems];
    const orderType = getLocalOrderTypeValue(localOrder);
    const grossTotal = nextCartItems.reduce(
      (sum, item) => sum + item.totalPrice,
      0,
    );
    const effectiveTaxSettings = taxSettings ?? {
      taxRate: 0,
      taxIncluded: false,
    };
    const subtotal = effectiveTaxSettings.taxIncluded
      ? nextCartItems.reduce(
          (sum, item) =>
            sum +
            calculatePriceExcludingTax(
              item.totalPrice,
              effectiveTaxSettings.taxRate,
              effectiveTaxSettings.taxIncluded,
            ),
          0,
        )
      : grossTotal;
    const taxTotal = effectiveTaxSettings.taxIncluded
      ? nextCartItems.reduce(
          (sum, item) =>
            sum +
            calculateItemTax(
              item.totalPrice,
              effectiveTaxSettings.taxRate,
              effectiveTaxSettings.taxIncluded,
            ),
          0,
        )
      : 0;
    const serviceChargeTotal = serviceChargePresets.reduce((sum, preset) => {
      const metadata = preset.metadata as
        | { orderTypes?: ("DINE_IN" | "TAKEAWAY" | "DELIVERY")[] }
        | null
        | undefined;
      if (
        metadata?.orderTypes?.length &&
        !metadata.orderTypes.includes(orderType)
      ) {
        return sum;
      }
      const calculationMode = preset.calculationMode?.toLowerCase();
      if (calculationMode === "percentage") {
        return sum + subtotal * (Number(preset.rate ?? 0) / 100);
      }
      if (calculationMode === "fixed") {
        return sum + Number(preset.amount ?? 0);
      }
      return sum;
    }, 0);
    const discountTotal = localOrder.totals.discountTotal;
    const totals = {
      subtotal: Math.round(subtotal * 100) / 100,
      discountTotal,
      taxTotal: Math.round(taxTotal * 100) / 100,
      serviceChargeTotal: Math.round(serviceChargeTotal * 100) / 100,
      grandTotal:
        Math.round(
          (subtotal + serviceChargeTotal + taxTotal - discountTotal) * 100,
        ) / 100,
    };

    const updated = await updateLocalOrder(
      localOrder.clientOrderId,
      {
        cartItems: nextCartItems,
        totals,
      },
      { addedCartItems: newItems },
    );
    const nextOrder = updated ?? {
      ...localOrder,
      cartItems: nextCartItems,
      totals,
    };
    setLocalOrder(nextOrder);
    try {
      await printLocalSupplementaryBillAndKitchen(nextOrder, newItems);
    } catch (printErr) {
      toast.error(
        printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
      );
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
      }),
      queryClient.invalidateQueries({
        queryKey: posKeys.branchTables(nextOrder.branchId),
      }),
    ]);
  };

  const handleRemoveLocalOrderItem = async (itemId: string) => {
    if (!localOrder) return;
    if (localOrder.status !== "OPEN") {
      toast.error("Local order ນີ້ບໍ່ສາມາດແກ້ໄຂລາຍການໄດ້ແລ້ວ");
      return;
    }
    const target = localOrder.cartItems.find((item) => item.id === itemId);
    if (!target) return;
    if (localOrder.cartItems.length <= 1) {
      toast.error("ລາຍການສຸດທ້າຍ ລຶບບໍ່ໄດ້ — ກະລຸນາຍົກເລີກ order ແທນ");
      return;
    }

    const targetName = `${target.productName}${
      target.variantName ? ` (${target.variantName})` : ""
    }`;
    const ok = await confirm({
      title: "ລຶບລາຍການ?",
      description: `ຈະລຶບ "${targetName}" ອອກຈາກ Order #L${localOrder.clientOrderNo}`,
      actionText: "ລຶບລາຍການ",
    });
    if (!ok) return;

    const nextCartItems = localOrder.cartItems.filter(
      (item) => item.id !== itemId,
    );
    const totals = calculateLocalTotals(
      nextCartItems,
      localOrder,
      localOrder.totals.discountTotal,
    );

    try {
      const updated = await updateLocalOrder(localOrder.clientOrderId, {
        cartItems: nextCartItems,
        totals,
      });
      const nextOrder = updated ?? {
        ...localOrder,
        cartItems: nextCartItems,
        totals,
      };
      setLocalOrder(nextOrder);
      toast.success("ລຶບລາຍການສຳເລັດ");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(nextOrder.branchId),
        }),
      ]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  const handleVoidLocalOrder = async (reason?: string) => {
    if (!localOrder) return;
    const trimmedReason = reason?.trim();
    if (!trimmedReason) {
      toast.error("ກະລຸນາປ້ອນເຫດຜົນການຍົກເລີກ");
      return;
    }

    setIsVoidingLocalOrder(true);
    try {
      const voided = await voidLocalOrder(localOrder.clientOrderId, {
        reason: trimmedReason,
        voidedAt: new Date().toISOString(),
        voidedBy: {
          userId: localOrder.selection.salespersonId,
          name: localOrder.selection.salespersonName,
        },
      });
      const nextOrder = voided ?? localOrder;
      setLocalOrder(nextOrder);
      setIsVoidDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(nextOrder.branchId),
        }),
      ]);
      toast.success("ຍົກເລີກ local order ສຳເລັດແລ້ວ");
      requestAutoSyncAfterAdjustment(toAutoSyncScope(nextOrder));

      try {
        await printLocalCancelKitchenNote(nextOrder);
      } catch (printErr) {
        toast.error(
          printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຍົກເລີກ",
      );
    } finally {
      setIsVoidingLocalOrder(false);
    }
  };

  const handleRefundLocalOrder = async () => {
    if (!localOrder) return;
    const methodId =
      localRefundMethodId || localOrder.payments[0]?.methodId || "";
    const reason = localRefundReason.trim();
    if (!methodId) {
      toast.error("ກະລຸນາເລືອກວິທີຄືນເງິນ");
      return;
    }
    if (!reason) {
      toast.error("ກະລຸນາປ້ອນເຫດຜົນການຄືນເງິນ");
      return;
    }

    setIsRefundingLocalOrder(true);
    try {
      const refunded = await refundLocalOrder(localOrder.clientOrderId, {
        methodId,
        reason,
        refundedAt: new Date().toISOString(),
        refundedBy: {
          userId: localOrder.selection.salespersonId,
          name: localOrder.selection.salespersonName,
        },
        returnStock: false,
      });
      const nextOrder = refunded ?? localOrder;
      setLocalOrder(nextOrder);
      setIsRefundDialogOpen(false);
      setLocalRefundReason("");
      setLocalRefundMethodId("");
      await queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
      });
      toast.success("ບັນທຶກ refund local order ສຳເລັດແລ້ວ — ລໍຖ້າ sync");
      requestAutoSyncAfterAdjustment(toAutoSyncScope(nextOrder));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການ refund",
      );
    } finally {
      setIsRefundingLocalOrder(false);
    }
  };

  const handleMoveLocalOrderTable = async (
    tableId: string | null,
    table?: { name?: string | null } | null,
  ) => {
    if (!localOrder) return;
    if (localOrder.status !== "OPEN") {
      toast.error("Local order ນີ້ບໍ່ສາມາດຍ້າຍໂຕະໄດ້ແລ້ວ");
      return;
    }

    try {
      const movedAt = new Date().toISOString();
      const moved = await moveLocalOrderTable(localOrder.clientOrderId, {
        toTableId: tableId ?? undefined,
        toTableName: table?.name ?? undefined,
        movedAt,
        movedBy: {
          userId: localOrder.selection.salespersonId,
          name: localOrder.selection.salespersonName,
        },
      });
      const nextOrder = moved ?? localOrder;
      setLocalOrder(nextOrder);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(nextOrder.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(nextOrder.branchId),
        }),
      ]);
      toast.success(
        tableId
          ? "ຍ້າຍໂຕະ local order ສຳເລັດແລ້ວ"
          : "ຍົກເລີກໂຕະ local order ສຳເລັດແລ້ວ",
      );
      requestAutoSyncAfterAdjustment(toAutoSyncScope(nextOrder));

      try {
        await printLocalMoveTableNote(nextOrder, {
          fromTableName: localOrder.selection.tableName,
          toTableName: nextOrder.selection.tableName,
          movedAt,
        });
      } catch (printErr) {
        toast.error(
          printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຍ້າຍໂຕະ",
      );
    }
  };

  const handleMergeLocalOrderIntoTarget = async (target: LocalOrderRecord) => {
    if (!localOrder) return;
    if (localOrder.status !== "OPEN" || target.status !== "OPEN") {
      toast.error("ລວມໄດ້ສະເພາະ local order ທີ່ຍັງເປີດຢູ່");
      return;
    }
    if (
      ["syncing", "failed", "needs_review"].includes(localOrder.syncStatus) ||
      ["syncing", "failed", "needs_review"].includes(target.syncStatus)
    ) {
      toast.error("Order ທີ່ກຳລັງ sync ຫຼື sync fail ຍັງບໍ່ຄວນລວມ");
      return;
    }

    const ok = await confirm({
      title: "ລວມ Order?",
      description: `ຈະລວມ Local Order #L${localOrder.clientOrderNo} ເຂົ້າ #L${target.clientOrderNo}. Order ຕົ້ນທາງຈະຖືກ mark ເປັນ MERGED.`,
      actionText: "ລວມ Order",
    });
    if (!ok) return;

    setIsMergingLocalOrder(true);
    try {
      const mergedAt = new Date().toISOString();
      const nextCartItems = [...target.cartItems, ...localOrder.cartItems];
      const totals = calculateLocalTotals(
        nextCartItems,
        target,
        target.totals.discountTotal + localOrder.totals.discountTotal,
      );
      const merged = await mergeLocalOrders({
        sourceClientOrderIds: [localOrder.clientOrderId],
        targetClientOrderId: target.clientOrderId,
        mergedAt,
        mergedBy: {
          userId: localOrder.selection.salespersonId,
          name: localOrder.selection.salespersonName,
        },
        totals,
      });
      setIsLocalMergeDialogOpen(false);
      setLocalMergeSearch("");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(localOrder.branchId),
        }),
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(localOrder.branchId),
        }),
      ]);
      toast.success("ລວມ local order ສຳເລັດແລ້ວ");
      requestAutoSyncAfterAdjustment(
        toAutoSyncScope(merged ?? target),
      );

      try {
        await printLocalMergeOrderNote(merged ?? target, [localOrder], {
          mergedAt,
        });
      } catch (printErr) {
        toast.error(
          printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
        );
      }

      navigate({
        to: "/app/pos/orders/$orderId",
        params: { orderId: merged?.clientOrderId ?? target.clientOrderId },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການລວມ order",
      );
    } finally {
      setIsMergingLocalOrder(false);
    }
  };

  const handleReprintLocalAdjustmentNote = async (
    operation: LocalOrderOperationRecord,
  ) => {
    if (!localOrder) return;
    const payload = readOperationPayload(operation);

    try {
      if (operation.type === "void_order") {
        await printLocalCancelKitchenNote(localOrder);
        return;
      }

      if (operation.type === "move_table") {
        await printLocalMoveTableNote(localOrder, {
          fromTableName:
            typeof payload.fromTableName === "string"
              ? payload.fromTableName
              : undefined,
          toTableName:
            typeof payload.toTableName === "string"
              ? payload.toTableName
              : undefined,
          movedAt:
            typeof payload.movedAt === "string" ? payload.movedAt : undefined,
          reason:
            typeof payload.reason === "string" ? payload.reason : undefined,
        });
        return;
      }

      if (operation.type === "merge_orders") {
        const targetClientOrderId =
          typeof payload.targetClientOrderId === "string"
            ? payload.targetClientOrderId
            : localOrder.mergedIntoClientOrderId;
        const targetOrder =
          (targetClientOrderId
            ? await getLocalOrder(targetClientOrderId)
            : undefined) ?? localOrder;
        const sourceIds = Array.isArray(payload.sourceClientOrderIds)
          ? payload.sourceClientOrderIds.filter(
              (value): value is string => typeof value === "string",
            )
          : typeof payload.sourceClientOrderId === "string"
            ? [payload.sourceClientOrderId]
            : localOrder.clientOrderId !== targetOrder.clientOrderId
              ? [localOrder.clientOrderId]
              : [];
        const sourceOrders = (
          await Promise.all(
            sourceIds.map((clientOrderId) => getLocalOrder(clientOrderId)),
          )
        ).filter((order): order is LocalOrderRecord => Boolean(order));

        await printLocalMergeOrderNote(
          targetOrder,
          sourceOrders.length > 0 ? sourceOrders : [localOrder],
          {
            mergedAt:
              typeof payload.mergedAt === "string"
                ? payload.mergedAt
                : operation.capturedAt,
            reason:
              typeof payload.reason === "string" ? payload.reason : undefined,
          },
        );
      }
    } catch (printErr) {
      toast.error(
        printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
      );
    }
  };

  const reloadLocalOrder = async () => {
    if (!isLocalOrderId) return;
    const [record, operations] = await Promise.all([
      getLocalOrder(orderId),
      listLocalOrderOperations(orderId),
    ]);
    setLocalOrder(record ?? null);
    setLocalOrderOperations(operations);
  };

  const handleRetryLocalSync = async () => {
    if (!localOrder) return;
    const updated = await markLocalOrderSyncStatus(localOrder.clientOrderId, {
      status: "PAID",
      syncStatus: "queued",
      lastSyncError: undefined,
    });
    setLocalOrder(updated ?? localOrder);
    toast.success("ສົ່ງ order ກັບເຂົ້າ queue sync ແລ້ວ");
    void runAutoSyncNow("manual", toAutoSyncScope(localOrder));
    await reloadLocalOrder();
  };

  const handleManualResolveLocalSync = async () => {
    if (!localOrder) return;
    const ok = await confirm({
      title: "Manual resolve local order?",
      description:
        "Action ນີ້ຈະ mark local order ນີ້ເປັນ synced ແລະບໍ່ສົ່ງໄປ sync ອີກ. ຄວນໃຊ້ຫຼັງຈາກກວດສອບ server/inbox ແລ້ວເທົ່ານັ້ນ.",
      actionText: "Manual resolve",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (!ok) return;

    const updated = await markLocalOrderSyncStatus(localOrder.clientOrderId, {
      status: "SYNCED",
      syncStatus: "synced",
      lastSyncError: undefined,
    });
    setLocalOrder(updated ?? localOrder);
    toast.success("manual resolve local order ແລ້ວ");
    await reloadLocalOrder();
  };

  const handleExportLocalSyncPayload = () => {
    if (!localOrder) return;
    const payload = buildSyncPayloadFromLocalOrder(localOrder);
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${localOrder.clientOrderId}-sync-payload.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    void navigator.clipboard?.writeText(json).catch(() => undefined);
    toast.success("export sync payload ແລ້ວ");
  };

  const handleRetryCustomerSync = async () => {
    if (!localOrder?.customerSnapshot?.localCustomerId) return;
    await retryLocalCustomerDraftSync(
      localOrder.customerSnapshot.localCustomerId,
    );
    await handleRetryLocalSync();
    toast.success("ສົ່ງ customer draft ກັບເຂົ້າ queue sync ແລ້ວ");
  };

  const openReplaceCustomerDialog = () => {
    if (!localOrder) return;
    setReplaceCustomerPhone(localOrder.customerSnapshot?.phone ?? "");
    setReplaceCustomerFirstName(localOrder.customerSnapshot?.firstName ?? "");
    setReplaceCustomerLastName(localOrder.customerSnapshot?.lastName ?? "");
    setReplaceCustomerId(localOrder.customerId ?? "");
    setIsReplaceCustomerDialogOpen(true);
  };

  const handleReplaceCustomerMapping = async () => {
    if (!localOrder) return;
    const phone = replaceCustomerPhone.trim();
    if (!phone) {
      toast.error("ກະລຸນາໃສ່ເບີໂທລູກຄ້າ");
      return;
    }

    setIsReplacingCustomer(true);
    try {
      const updated = await replaceLocalOrderCustomerMapping(
        localOrder.clientOrderId,
        {
          phone,
          firstName: replaceCustomerFirstName.trim() || undefined,
          lastName: replaceCustomerLastName.trim() || undefined,
          customerId: replaceCustomerId.trim() || undefined,
          source: replaceCustomerId.trim() ? "server" : "cache",
        },
      );
      setLocalOrder(updated ?? localOrder);
      setIsReplaceCustomerDialogOpen(false);
      toast.success("ອັບເດດ customer mapping ແລ້ວ");
      await reloadLocalOrder();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "ອັບເດດ customer mapping ບໍ່ສຳເລັດ",
      );
    } finally {
      setIsReplacingCustomer(false);
    }
  };

  const handleRemoveLocalReward = async () => {
    if (!localOrder) return;

    const confirmed = await confirm({
      title: "ຖອນການແລກລາງວັນ",
      description:
        "ຈະລຶບສ່ວນຫຼຸດຈາກລາງວັນແລະຄິດຍອດໃໝ່ ຄະແນນທີ່ຈອງໄວ້ຊົ່ວຄາວຈະປ່ອຍກັບ",
      actionText: "ຖອນລາງວັນ",
      cancelText: "ຍົກເລີກ",
    });
    if (!confirmed) return;

    setIsRemovingLocalReward(true);
    try {
      const baseTotals = calculateLocalTotals(
        localOrder.cartItems,
        localOrder,
        0,
      );
      const updated = await clearLocalOrderReward(localOrder.clientOrderId, {
        subtotal: baseTotals.subtotal,
        taxTotal: baseTotals.taxTotal,
        serviceChargeTotal: baseTotals.serviceChargeTotal,
      });
      if (updated) {
        setLocalOrder(updated);
      }
      toast.success("ຖອນການແລກລາງວັນແລ້ວ");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "ຖອນການແລກລາງວັນ ບໍ່ສຳເລັດ",
      );
    } finally {
      setIsRemovingLocalReward(false);
    }
  };

  const localCouponConflict =
    localOrder != null && isLocalOrderCouponConflict(localOrder);

  if (isLocalOrderId) {
    if (isLocalOrderLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader />
        </div>
      );
    }

    if (!localOrder) {
      return (
        <div className="flex h-full items-center justify-center p-4">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <X className="h-8 w-8 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ບໍ່ພົບ Local Order</EmptyTitle>
              <EmptyDescription>ບໍ່ສາມາດພົບ Order local ນີ້ໄດ້</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      );
    }

    const localOrderType = getLocalOrderTypeValue(localOrder);
    const localDerivedStatus = getLocalDerivedStatus(localOrder);
    const canRemoveLocalReward =
      localOrder.status === "OPEN" && localOrderHasReward(localOrder);
    const localRefundTotal = localOrder.refundTotal ?? 0;
    const localRefundRemaining = Math.max(
      0,
      localOrder.totals.grandTotal - localRefundTotal,
    );
    const canRefundLocalOrder =
      localOrder.status === "PAID" && localRefundRemaining > 0;
    const filteredLocalMergeCandidates = localMergeCandidates.filter(
      (candidate) => {
        const searchText = localMergeSearch.trim().toLowerCase();
        if (!searchText) return true;
        return (
          String(candidate.clientOrderNo).includes(searchText) ||
          candidate.clientOrderId.toLowerCase().includes(searchText) ||
          (candidate.selection.tableName ?? "")
            .toLowerCase()
            .includes(searchText)
        );
      },
    );

    return (
      <>
        <OrderDetailLayout
          header={
            <OrderDetailHeader
              title={`Order #L${localOrder.clientOrderNo}`}
              subtitle={formatDateTimeShort(localOrder.createdAt)}
              onBack={goBack}
              badges={
                <>
                  <Badge className={getOrderStatusBadgeClass(localDerivedStatus)}>
                    {getOrderStatusLabel(localDerivedStatus)}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={getSyncStatusBadgeClass(localOrder.syncStatus)}
                  >
                    {getSyncStatusLabel(localOrder.syncStatus)}
                  </Badge>
                </>
              }
            />
          }
          sidebar={
            <>
              <OrderDetailSummary
                subtotal={localOrder.totals.subtotal}
                taxTotal={localOrder.totals.taxTotal}
                serviceChargeTotal={localOrder.totals.serviceChargeTotal}
                discountTotal={localOrder.totals.discountTotal}
                discountLines={
                  hasDiscountBreakdown(localOrder.discountBreakdown)
                    ? localOrder.discountBreakdown?.map((item) => ({
                        key: item.id,
                        label: item.label,
                        amount: item.amount,
                        variant: "discount" as const,
                      }))
                    : undefined
                }
                grandTotal={localOrder.totals.grandTotal}
              />

              {localOrder.payments.length > 0 && (
                <OrderDetailSection title="ວິທີຊຳລະ" icon={CreditCard}>
                  <ul className="space-y-2">
                    {localOrder.payments.map((payment, index) => (
                      <li
                        key={`${payment.methodId}-${index}`}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-muted-foreground">
                          {paymentMethodNameById.get(payment.methodId) ??
                            "Payment"}
                        </span>
                        <span className="font-medium tabular-nums">
                          {payment.amountGross.toLocaleString("lo-LA")} ₭
                        </span>
                      </li>
                    ))}
                  </ul>
                </OrderDetailSection>
              )}

              {(localOrder.refunds?.length ?? 0) > 0 && (
                <OrderDetailSection title="ປະຫວັດຄືນເງິນ" icon={RefreshCw}>
                  <ul className="space-y-3">
                    {localOrder.refunds?.map((refund) => (
                      <li
                        key={refund.refundId}
                        className="rounded-lg border bg-background/60 p-3 text-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium">
                            {paymentMethodNameById.get(refund.methodId) ??
                              "Payment"}
                          </div>
                          <div className="font-semibold tabular-nums">
                            {refund.amount.toLocaleString("lo-LA")} ₭
                          </div>
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs">
                          {formatDateTimeShort(refund.refundedAt)}
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs">
                          ເຫດຜົນ: {refund.reason}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex items-center justify-between border-t pt-3 text-sm">
                    <span className="font-medium">ຍອດຄືນເງິນລວມ</span>
                    <span className="font-semibold tabular-nums">
                      {localRefundTotal.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                </OrderDetailSection>
              )}

              <OrderDetailActions>
                {(localOrder.syncStatus === "failed" ||
                  localOrder.syncStatus === "needs_review") && (
                  <OrderDetailActionGroup title="Sync">
                    <Button
                      className="w-full"
                      onClick={handleRetryLocalSync}
                      disabled={isCompletingLocalOrder}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Retry sync
                    </Button>
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={handleManualResolveLocalSync}
                      disabled={isCompletingLocalOrder}
                    >
                      {localCouponConflict
                        ? "Manager resolve coupon conflict"
                        : "Manual resolve"}
                    </Button>
                  </OrderDetailActionGroup>
                )}
                {localOrder.status === "OPEN" && (
                  <OrderDetailActionGroup title="Order">
                    <Button
                      className="h-11 w-full font-semibold"
                      onClick={() => {
                        if (!branchId || !localOrder) return;
                        clearCustomerDisplayTempCart();
                        setCustomerDisplayOrder(
                          localOrder.clientOrderId,
                          branchId,
                          {
                            items: cartSnapshotsToDisplayCartItems(
                              localOrder.cartItems,
                            ),
                            grandTotal: localOrder.totals.grandTotal,
                            discountTotal: localOrder.totals.discountTotal,
                          },
                        );
                        setIsPaymentDialogOpen(true);
                      }}
                      disabled={isCompletingLocalOrder || isVoidingLocalOrder}
                    >
                      <DollarSign className="mr-2 h-4 w-4" />
                      ຈ່າຍເງິນ
                    </Button>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setIsAddItemsDialogOpen(true)}
                      disabled={isCompletingLocalOrder}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      ເພີ່ມລາຍການ
                    </Button>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setIsTableDialogOpen(true)}
                      disabled={isCompletingLocalOrder || isVoidingLocalOrder}
                    >
                      <Table className="mr-2 h-4 w-4" />
                      {localOrder.selection.tableName ? "ຍ້າຍໂຕະ" : "ຜູກໂຕະ"}
                    </Button>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setIsLocalMergeDialogOpen(true)}
                      disabled={isMergingLocalOrder}
                    >
                      <Move className="mr-2 h-4 w-4" />
                      ລວມ Order
                    </Button>
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={() => setIsVoidDialogOpen(true)}
                      disabled={isCompletingLocalOrder || isVoidingLocalOrder}
                    >
                      <X className="mr-2 h-4 w-4" />
                      ຍົກເລີກ Order
                    </Button>
                  </OrderDetailActionGroup>
                )}
                <OrderDetailActionGroup title="Print">
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => printLocalProformaOnly(localOrder)}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    ພິມ Proforma
                  </Button>
                  {localOrder.status === "PAID" && (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => printLocalReceiptOnly(localOrder)}
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      ພິມ Receipt
                    </Button>
                  )}
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => printLocalKitchenOnly(localOrder)}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    ພິມ Kitchen
                  </Button>
                </OrderDetailActionGroup>
                {canRefundLocalOrder && (
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={() => {
                      setLocalRefundMethodId(
                        localOrder.payments[0]?.methodId ?? "",
                      );
                      setIsRefundDialogOpen(true);
                    }}
                    disabled={isRefundingLocalOrder}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    ຄືນເງິນ
                  </Button>
                )}
                {(localOrder.customerSnapshot?.localCustomerId &&
                  localOrder.customerSyncStatus !== "synced") ||
                localOrder.customerSnapshot ||
                localOrder.customerId ? (
                  <OrderDetailActionGroup title="Advanced">
                    {localOrder.customerSnapshot?.localCustomerId &&
                      localOrder.customerSyncStatus !== "synced" && (
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={handleRetryCustomerSync}
                          disabled={isCompletingLocalOrder}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Retry customer sync
                        </Button>
                      )}
                    {(localOrder.customerSnapshot || localOrder.customerId) && (
                      <Button
                        className="w-full"
                        variant="outline"
                        onClick={openReplaceCustomerDialog}
                        disabled={isCompletingLocalOrder || isReplacingCustomer}
                      >
                        Replace customer mapping
                      </Button>
                    )}
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={handleExportLocalSyncPayload}
                    >
                      Export sync payload
                    </Button>
                  </OrderDetailActionGroup>
                ) : null}
              </OrderDetailActions>
            </>
          }
        >
          <OrderDetailSection title="ຂໍ້ມູນ Order">
            <OrderDetailMetaGrid
              items={[
                {
                  icon: ShoppingBag,
                  label: "ປະເພດ",
                  value: getOrderTypeLabel(localOrderType),
                },
                ...(localOrder.selection.tableName
                  ? [
                      {
                        icon: Table,
                        label: "ໂຕະ",
                        value: localOrder.selection.tableName,
                      },
                    ]
                  : []),
                ...(localOrder.selection.salespersonName
                  ? [
                      {
                        icon: User,
                        label: "ຄົນຂາຍ",
                        value: localOrder.selection.salespersonName,
                      },
                    ]
                  : []),
              ]}
            />

            {(localOrder.syncStatus !== "synced" || localOrder.lastSyncError) && (
              <div className="mt-4 space-y-2 rounded-lg border border-dashed bg-muted/20 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">Local sync</span>
                  <Badge
                    variant="outline"
                    className={getSyncStatusBadgeClass(localOrder.syncStatus)}
                  >
                    {getSyncStatusLabel(localOrder.syncStatus)}
                  </Badge>
                </div>
                {localOrder.lastSyncError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-destructive text-xs">
                    {localOrder.lastSyncError}
                  </div>
                )}
              </div>
            )}

            {(localOrder.customerSnapshot || localOrder.customerId) && (
              <div className="mt-4 rounded-lg border bg-muted/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <Phone className="h-4 w-4 text-primary" />
                    ລູກຄ້າ / ສະມາຊິກ
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {localOrder.customerSnapshot?.source && (
                      <Badge
                        variant="outline"
                        className={getCustomerSourceColor(
                          localOrder.customerSnapshot.source,
                        )}
                      >
                        {getCustomerSourceLabel(
                          localOrder.customerSnapshot.source,
                        )}
                      </Badge>
                    )}
                    {localOrder.customerSyncStatus && (
                      <Badge
                        variant="outline"
                        className={getCustomerSyncStatusColor(
                          localOrder.customerSyncStatus,
                        )}
                      >
                        {getCustomerSyncStatusLabel(
                          localOrder.customerSyncStatus,
                        )}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-sm">
                  {localOrder.customerSnapshot?.phone && (
                    <div>ເບີໂທ: {localOrder.customerSnapshot.phone}</div>
                  )}
                  {(localOrder.customerSnapshot?.firstName ||
                    localOrder.customerSnapshot?.lastName) && (
                    <div>
                      ຊື່: {localOrder.customerSnapshot.firstName}{" "}
                      {localOrder.customerSnapshot.lastName}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4">
              <LocalOrderPricingPanel
                couponSnapshots={localOrder.couponSnapshots}
                voucherSnapshots={localOrder.voucherSnapshots}
                rewardSnapshot={localOrder.rewardSnapshot}
                discountBreakdown={localOrder.discountBreakdown}
                pricingSource={localOrder.pricingSource}
                canRemoveReward={canRemoveLocalReward}
                isRemovingReward={isRemovingLocalReward}
                onRemoveReward={handleRemoveLocalReward}
              />
            </div>

            {localOrder.isCompacted && (
              <div className="mt-4 flex flex-col gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-blue-900 text-sm dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
                <p>
                  ລາຍລະອຽດສິນຄ້າຖືກລຶບອອກຈາກເຄື່ອງເພື່ອປະຫຍັດບ່ອນເກັບ — ຂໍ້ມູນຂາຍຢູ່ server
                  {localOrder.compactedAt
                    ? ` (${formatDateTimeShort(localOrder.compactedAt)})`
                    : ""}
                </p>
                {localOrder.serverOrderId ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="w-fit"
                    onClick={() =>
                      navigate({
                        to: "/app/pos/orders/$orderId",
                        params: { orderId: localOrder.serverOrderId! },
                      })
                    }
                  >
                    ເປີດບິນ server
                  </Button>
                ) : null}
              </div>
            )}

            {localCouponConflict && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                Coupon sync conflict — ກວດ replay result ແລະ resolve ກ່ອນປິດກະ
              </div>
            )}

            {localOrder.voidedAt && (
              <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <div className="font-medium text-destructive">ຍົກເລີກແລ້ວ</div>
                <div className="mt-1 text-muted-foreground">
                  ເວລາ: {formatDateTimeShort(localOrder.voidedAt)}
                </div>
                {localOrder.voidReason && (
                  <div className="mt-1 text-muted-foreground">
                    ເຫດຜົນ: {localOrder.voidReason}
                  </div>
                )}
              </div>
            )}

            {localOrder.mergedIntoClientOrderId && (
              <div className="mt-4 rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm dark:border-violet-800 dark:bg-violet-950/30">
                <div className="font-medium text-violet-800 dark:text-violet-200">
                  ລວມບິນແລ້ວ
                </div>
                {localOrder.mergedAt && (
                  <div className="mt-1 text-muted-foreground">
                    ເວລາ: {formatDateTimeShort(localOrder.mergedAt)}
                  </div>
                )}
              </div>
            )}
          </OrderDetailSection>

          <OrderDetailSection
            title="ລາຍການສິນຄ້າ"
            description={
              localOrder.isCompacted
                ? "ຖືກລຶບອອກຈາກເຄື່ອງ (compact)"
                : `${localOrder.cartItems.length} ລາຍການ`
            }
          >
            <OrderDetailItemsList
              isEmpty={
                localOrder.isCompacted || localOrder.cartItems.length === 0
              }
            >
              {localOrder.isCompacted ? null : localOrder.cartItems.map((item) => (
                <OrderDetailItemRow
                  key={item.id}
                  name={
                    <>
                      {item.productName}
                      {item.variantName ? ` (${item.variantName})` : ""}
                    </>
                  }
                  details={
                    <>
                      {item.modifiers.length > 0 && (
                        <div className="space-y-0.5">
                          {item.modifiers.map((modifier) => (
                            <p
                              key={`${item.id}-${modifier.modifierId}-${modifier.optionValue}`}
                              className="text-muted-foreground text-xs"
                            >
                              + {modifier.optionLabel || modifier.modifierName}
                            </p>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <p className="text-muted-foreground text-xs italic">
                          {item.note}
                        </p>
                      )}
                    </>
                  }
                  quantityLine={`${item.quantity} x ${item.price.toLocaleString("lo-LA")} ₭`}
                  total={`${item.totalPrice.toLocaleString("lo-LA")} ₭`}
                  actions={
                    localOrder.status === "OPEN" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          void handleRemoveLocalOrderItem(item.id)
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : undefined
                  }
                />
              ))}
            </OrderDetailItemsList>
          </OrderDetailSection>

          {localOrderOperations.length > 0 && (
            <OrderDetailSection
              title="Audit timeline"
              description="ລຳດັບ action ຂອງ order ນີ້"
              action={
                <Badge variant="outline">
                  {localOrderOperations.length} events
                </Badge>
              }
              variant="muted"
            >
                  <div className="relative space-y-0">
                    <div className="absolute top-3 bottom-3 left-[11px] w-px bg-border" />
                    {localOrderOperations.map((operation, index) => {
                      const operationTime =
                        operation.capturedAt ?? operation.createdAt;
                      const isLast = index === localOrderOperations.length - 1;
                      return (
                        <div
                          key={operation.operationId}
                          className={cn(
                            "relative flex gap-3 pb-5",
                            isLast && "pb-0",
                          )}
                        >
                          <div
                            className={cn(
                              "relative z-10 mt-1 size-6 rounded-full border-2 bg-background",
                              operation.syncStatus === "synced" &&
                                "border-green-500",
                              (operation.syncStatus === "queued" ||
                                operation.syncStatus === "syncing") &&
                                "border-amber-500",
                              (operation.syncStatus === "failed" ||
                                operation.syncStatus === "needs_review") &&
                                "border-destructive",
                              (!operation.syncStatus ||
                                operation.syncStatus === "not_ready") &&
                                "border-muted-foreground/40",
                            )}
                          >
                            <span
                              className={cn(
                                "absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full",
                                operation.syncStatus === "synced" &&
                                  "bg-green-500",
                                (operation.syncStatus === "queued" ||
                                  operation.syncStatus === "syncing") &&
                                  "bg-amber-500",
                                (operation.syncStatus === "failed" ||
                                  operation.syncStatus === "needs_review") &&
                                  "bg-destructive",
                                (!operation.syncStatus ||
                                  operation.syncStatus === "not_ready") &&
                                  "bg-muted-foreground/50",
                              )}
                            />
                          </div>
                          <div className="min-w-0 flex-1 rounded-md border bg-background p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-medium">
                                  {getOperationLabel(operation.type)}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-xs">
                                  <span>
                                    {formatDateTimeShort(operationTime)}
                                  </span>
                                  {operation.performedBy?.name && (
                                    <>
                                      <span>•</span>
                                      <span>
                                        by {operation.performedBy.name}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {canReprintAdjustmentNote(operation.type) && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleReprintLocalAdjustmentNote(
                                        operation,
                                      )
                                    }
                                  >
                                    <Printer className="mr-1 h-3 w-3" />
                                    Reprint note
                                  </Button>
                                )}
                                <Badge
                                  variant="outline"
                                  className={getOperationSyncColor(
                                    operation.syncStatus,
                                  )}
                                >
                                  {operation.syncStatus ?? "not_ready"}
                                </Badge>
                              </div>
                            </div>
                            <div className="mt-2 truncate font-mono text-[11px] text-muted-foreground">
                              {operation.operationId}
                            </div>
                            {operation.lastSyncError && (
                              <div className="mt-2 rounded border border-destructive/30 bg-destructive/5 p-2 text-destructive text-xs">
                                {operation.lastSyncError}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
            </OrderDetailSection>
          )}
        </OrderDetailLayout>

        <PaymentDialog
          open={isPaymentDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              clearCustomerDisplayOrder();
              clearCustomerDisplayCustomer();
            }
            setIsPaymentDialogOpen(open);
          }}
          orderTotal={localOrder.totals.grandTotal}
          onComplete={handleCompleteLocalOrder}
          isSubmitting={isCompletingLocalOrder}
          branchId={branchId}
          customerId={localOrder.customerId}
          allowLocalCustomerDraft
          deviceId={localOrder.deviceId}
          initialCustomer={
            localOrder.customerSnapshot || localOrder.customerId
              ? {
                  id:
                    localOrder.customerId ??
                    localOrder.customerSnapshot?.customerId ??
                    "",
                  localCustomerId: localOrder.customerSnapshot?.localCustomerId,
                  firstName: localOrder.customerSnapshot?.firstName ?? "",
                  lastName: localOrder.customerSnapshot?.lastName ?? "",
                  phone: localOrder.customerSnapshot?.phone ?? "",
                  source: localOrder.customerSnapshot?.source ?? "server",
                  capturedAt: localOrder.customerSnapshot?.capturedAt,
                }
              : null
          }
          voucherSectionDefaultOpen={true}
          orderItems={localOrder.cartItems.map((item) => ({
            id: item.id,
            productName: item.productName,
            lineSubtotal: item.totalPrice,
            taxTotal: 0,
            variantId: item.variantId,
            quantity: item.quantity,
          }))}
        />
        <AddItemsDialog
          open={isAddItemsDialogOpen}
          onOpenChange={setIsAddItemsDialogOpen}
          orderId={localOrder.clientOrderId}
          onLocalSubmit={handleAddItemsToLocalOrder}
          onSuccess={() => {
            setIsAddItemsDialogOpen(false);
          }}
        />
        <TableSelectorDialog
          open={isTableDialogOpen}
          onOpenChange={setIsTableDialogOpen}
          branchId={branchId}
          currentTableId={localOrder.selection.tableId ?? null}
          onSelect={handleMoveLocalOrderTable}
        />
        <Dialog
          open={isReplaceCustomerDialogOpen}
          onOpenChange={setIsReplaceCustomerDialogOpen}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Replace customer mapping</DialogTitle>
              <DialogDescription>
                ອັບເດດເບີໂທ/ຊື່ ຫຼື server customerId ສຳລັບ order ນີ້
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="replaceCustomerPhone">ເບີໂທ</Label>
                <Input
                  id="replaceCustomerPhone"
                  value={replaceCustomerPhone}
                  onChange={(event) =>
                    setReplaceCustomerPhone(event.target.value)
                  }
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="replaceCustomerFirstName">ຊື່</Label>
                  <Input
                    id="replaceCustomerFirstName"
                    value={replaceCustomerFirstName}
                    onChange={(event) =>
                      setReplaceCustomerFirstName(event.target.value)
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="replaceCustomerLastName">ນາມສະກຸນ</Label>
                  <Input
                    id="replaceCustomerLastName"
                    value={replaceCustomerLastName}
                    onChange={(event) =>
                      setReplaceCustomerLastName(event.target.value)
                    }
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="replaceCustomerId">Server customerId (optional)</Label>
                <Input
                  id="replaceCustomerId"
                  value={replaceCustomerId}
                  onChange={(event) =>
                    setReplaceCustomerId(event.target.value)
                  }
                  className="mt-1 font-mono text-sm"
                  placeholder="uuid จาก server"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsReplaceCustomerDialogOpen(false)}
                disabled={isReplacingCustomer}
              >
                ຍົກເລີກ
              </Button>
              <Button
                onClick={handleReplaceCustomerMapping}
                disabled={isReplacingCustomer}
              >
                {isReplacingCustomer ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={isLocalMergeDialogOpen}
          onOpenChange={(open) => {
            setIsLocalMergeDialogOpen(open);
            if (!open) setLocalMergeSearch("");
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>ລວມ Local Order</DialogTitle>
              <DialogDescription>
                ເລືອກ local order ປາຍທາງທີ່ຈະຮັບລາຍການຈາກ #L
                {localOrder.clientOrderNo}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={localMergeSearch}
                  onChange={(event) => setLocalMergeSearch(event.target.value)}
                  placeholder="ຄົ້ນຫາ order ຫຼື ໂຕະ..."
                  className="pl-9"
                />
              </div>
              {filteredLocalMergeCandidates.length === 0 ? (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Search className="h-8 w-8 text-muted-foreground" />
                    </EmptyMedia>
                    <EmptyTitle>ບໍ່ພົບ Order</EmptyTitle>
                    <EmptyDescription>
                      ບໍ່ມີ local open order ທີ່ສາມາດລວມໄດ້
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : (
                <ScrollArea className="max-h-[360px]">
                  <div className="space-y-2">
                    {filteredLocalMergeCandidates.map((candidate) => (
                      <button
                        key={candidate.clientOrderId}
                        type="button"
                        onClick={() =>
                          handleMergeLocalOrderIntoTarget(candidate)
                        }
                        disabled={isMergingLocalOrder}
                        className="flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <div>
                          <div className="font-medium">
                            Local Order #L{candidate.clientOrderNo}
                          </div>
                          <div className="mt-1 text-muted-foreground text-xs">
                            {candidate.selection.tableName
                              ? `ໂຕະ: ${candidate.selection.tableName}`
                              : getOrderTypeLabel(
                                  getLocalOrderTypeValue(candidate),
                                )}
                            <span className="mx-2">•</span>
                            {candidate.totals.grandTotal.toLocaleString(
                              "lo-LA",
                            )}{" "}
                            ₭
                          </div>
                        </div>
                        <Move className="h-4 w-4 text-primary" />
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsLocalMergeDialogOpen(false)}
                disabled={isMergingLocalOrder}
              >
                ຍົກເລີກ
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <VoidOrderDialog
          open={isVoidDialogOpen}
          onOpenChange={setIsVoidDialogOpen}
          orderNo={localOrder.clientOrderNo}
          orderStatus={localOrder.status}
          onConfirm={handleVoidLocalOrder}
          requireReason={true}
          isSubmitting={isVoidingLocalOrder}
        />
        <Dialog
          open={isRefundDialogOpen}
          onOpenChange={(open) => {
            setIsRefundDialogOpen(open);
            if (!open) {
              setLocalRefundReason("");
              setLocalRefundMethodId("");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>ຄືນເງິນ Local Order</DialogTitle>
              <DialogDescription>
                ຄືນເງິນເຕັມຈຳນວນ {localRefundRemaining.toLocaleString("lo-LA")} ₭
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ຍອດບິນ</span>
                  <span>
                    {localOrder.totals.grandTotal.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">ຄືນແລ້ວ</span>
                  <span>{localRefundTotal.toLocaleString("lo-LA")} ₭</span>
                </div>
                <div className="mt-2 flex items-center justify-between border-t pt-2 font-semibold">
                  <span>ຈະຄືນຄັ້ງນີ້</span>
                  <span>{localRefundRemaining.toLocaleString("lo-LA")} ₭</span>
                </div>
              </div>
              <div>
                <label
                  htmlFor="local-refund-method"
                  className="mb-2 block font-medium text-sm"
                >
                  ວິທີຄືນເງິນ
                </label>
                <Select
                  value={localRefundMethodId}
                  onValueChange={setLocalRefundMethodId}
                >
                  <SelectTrigger id="local-refund-method">
                    <SelectValue placeholder="ເລືອກວິທີຄືນເງິນ" />
                  </SelectTrigger>
                  <SelectContent>
                    {localOrder.payments.map((payment, index) => (
                      <SelectItem
                        key={`${payment.methodId}-${index}`}
                        value={payment.methodId}
                      >
                        {paymentMethodNameById.get(payment.methodId) ??
                          payment.methodId}{" "}
                        ({payment.amountGross.toLocaleString("lo-LA")} ₭)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label
                  htmlFor="local-refund-reason"
                  className="mb-2 block font-medium text-sm"
                >
                  ເຫດຜົນ (ຈຳເປັນ)
                </label>
                <Input
                  id="local-refund-reason"
                  value={localRefundReason}
                  onChange={(event) => setLocalRefundReason(event.target.value)}
                  placeholder="ປ້ອນເຫດຜົນການຄືນເງິນ..."
                />
              </div>
              <p className="text-muted-foreground text-xs">
                MVP ຈະບັນທຶກ full refund ແລະບໍ່ຄືນ stock ອັດຕະໂນມັດ.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsRefundDialogOpen(false)}
                disabled={isRefundingLocalOrder}
              >
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleRefundLocalOrder}
                disabled={
                  isRefundingLocalOrder ||
                  !localRefundReason.trim() ||
                  !localRefundMethodId ||
                  localRefundRemaining <= 0
                }
              >
                {isRefundingLocalOrder ? "ກຳລັງບັນທຶກ..." : "ຢືນຢັນຄືນເງິນ"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <X className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ພົບ Order</EmptyTitle>
            <EmptyDescription>ບໍ່ສາມາດພົບ Order ນີ້ໄດ້</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const { order, items, tableName, salespersonName, delivery } = orderData;
  const discounts =
    (
      orderData as typeof orderData & {
        discounts?: Array<{
          id: string;
          label: string;
          amount: number | string;
        }>;
      }
    ).discounts ?? [];
  const customerData =
    (
      orderData as typeof orderData & {
        customer?: {
          firstName: string;
          lastName: string;
          phone: string | null;
        } | null;
      }
    ).customer ?? null;
  const canVoid = order.status === "OPEN"; // Only OPEN orders can be voided
  const canRefund = order.status === "PAID";
  const canReprint = order.status === "PAID" || order.status === "VOID";
  const canUpdateTable =
    order.status === "OPEN" && order.orderType === "DINE_IN";
  const canMoveItems = order.status === "OPEN" && items.length > 0;

  const handleTableSelect = async (tableId: string | null) => {
    if (!branchId) return;

    try {
      await updateTableMutation.mutateAsync({
        orderId: order.id,
        branchId,
        tableId,
      });
      toast.success(tableId ? "ຜູກໂຕະສຳເລັດແລ້ວ" : "ຍົກເລີກການຜູກໂຕະສຳເລັດແລ້ວ");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການອັບເດດໂຕະ",
      );
    }
  };

  const handleOrderSelect = async (targetOrderId: string) => {
    if (!branchId) return;

    try {
      await moveItemsMutation.mutateAsync({
        sourceOrderId: order.id,
        branchId,
        targetOrderId,
      });
      toast.success("ຍ້າຍລາຍການສຳເລັດແລ້ວ");
      // Navigate to target order detail
      navigate({
        to: "/app/pos/orders/$orderId",
        params: { orderId: targetOrderId },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຍ້າຍລາຍການ",
      );
    }
  };

  const handlePrintReceipt = async () => {
    if (!branchId || !orderId) return;

    // Validate order can be completed
    try {
      const validation = await posApi.validateOrderCompletion(
        orderId,
        branchId,
      );
      if (!validation.valid) {
        toast.error(validation.errors.join(", "));
        return;
      }
      clearCustomerDisplayTempCart();
      setCustomerDisplayOrder(orderId, branchId);
      setIsPaymentDialogOpen(true);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການກວດສອບ Order",
      );
    }
  };

  const handleCompleteOrder = async (
    payments: PaymentInput[],
    voucherCodes?: string[],
    couponCode?: string,
    couponOrderItemIds?: string[],
    couponApplications?: Array<{ code: string; orderItemIds: string[] }>,
    customerId?: string,
    rewardId?: string,
  ) => {
    if (!branchId || !orderId) return;

    paymentUiLog.info("server order complete start", {
      orderId,
      branchId,
      paymentCount: payments.length,
    });
    setIsPaymentDialogOpen(false);
    try {
      await completeOrderMutation.mutateAsync({
        orderId,
        branchId,
        deviceId: deviceId ?? undefined,
        payments,
        voucherCodes,
        couponCode,
        couponOrderItemIds,
        couponApplications,
        customerId,
        rewardId,
      });
      paymentUiLog.info("server order complete success", { orderId, branchId });
      toast.success("ສຳເລັດ Order ແລ້ວ");
      clearCustomerDisplayOrder();
      scheduleDeferredPrint(() => {
        return printReceiptOnly(orderId, branchId).catch((printErr) => {
          paymentUiLog.error("server order print failed", {
            orderId,
            branchId,
            errorMessage:
              printErr instanceof Error ? printErr.message : String(printErr),
          });
          toast.error(
            printErr instanceof Error ? printErr.message : String(printErr),
          );
        });
      });
    } catch (error) {
      paymentUiLog.error("server order complete failed", {
        orderId,
        branchId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      if (
        error instanceof ApiUserError &&
        error.code === "INSUFFICIENT_STOCK"
      ) {
        await confirm({
          title: "ສຕ໋ອກບໍ່ພໍຄັງ",
          description: error.message,
          actionText: "ຕົກລົງ",
        });
        return;
      }
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການສຳເລັດ Order",
      );
    }
  };

  const handleVoidOrder = async (reason?: string) => {
    if (!branchId || !orderId) return;

    try {
      await voidOrderMutation.mutateAsync({
        orderId,
        branchId,
        reason,
      });
      toast.success("ຍົກເລີກ Order ສຳເລັດແລ້ວ");
      setIsVoidDialogOpen(false);
      // Navigate back to orders list
      navigate({
        to: "/app/pos/orders",
        search: { page: 1, sortField: "createdAt", sortOrder: "desc" },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການຍົກເລີກ Order",
      );
    }
  };

  const handlePrintReceiptOnly = async () => {
    if (!branchId || !orderId) return;
    try {
      await printReceiptOnly(orderId, branchId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ");
    }
  };

  const handlePrintKitchenOnly = async () => {
    if (!branchId || !orderId) return;
    try {
      await printKitchenOnly(orderId, branchId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ");
    }
  };

  const handlePrintProformaOnly = async () => {
    if (!branchId || !orderId) return;
    try {
      await printProformaOnly(orderId, branchId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ");
    }
  };

  const serverGrandTotal =
    Number(order.subtotal) +
    Number(order.serviceChargeTotal) +
    Number(order.taxTotal) -
    Number(order.discountTotal);

  return (
    <>
      <OrderDetailLayout
        header={
          <OrderDetailHeader
            title={`Order #${order.orderNo}`}
            subtitle={formatDateTimeShort(order.createdAt)}
            onBack={goBack}
            badges={
              <>
                <Badge className={getOrderStatusBadgeClass(order.status)}>
                  {getOrderStatusLabel(order.status)}
                </Badge>
                <Badge variant="outline">
                  {getOrderTypeLabel(order.orderType)}
                </Badge>
              </>
            }
          />
        }
        sidebar={
          <>
            <OrderDetailSummary
              subtotal={Number(order.subtotal)}
              taxTotal={Number(order.taxTotal)}
              serviceChargeTotal={Number(order.serviceChargeTotal)}
              discountTotal={Number(order.discountTotal)}
              discountLines={
                discounts.length > 0
                  ? discounts.map((d) => ({
                      key: d.id,
                      label: d.label,
                      amount: Math.abs(Number(d.amount)),
                      variant: "discount" as const,
                    }))
                  : undefined
              }
              grandTotal={serverGrandTotal}
            />

            {(order.status === "PAID" ||
              order.status === "REFUNDED" ||
              orderPayments.length > 0) && (
              <OrderDetailSection title="ວິທີຊຳລະ" icon={CreditCard}>
                {isOrderPaymentsLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader />
                  </div>
                ) : orderPayments.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ບໍ່ມີຂໍ້ມູນການຊຳລະ
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {orderPayments.map((p) => {
                      const change =
                        p.changeGiven != null ? Number(p.changeGiven) : 0;
                      const showChange = change > 0;
                      return (
                        <li key={p.id} className="text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground">
                              {p.method.displayName}
                            </span>
                            <span className="shrink-0 font-medium tabular-nums">
                              {Number(p.amountGross).toLocaleString("lo-LA")} ₭
                            </span>
                          </div>
                          {showChange && (
                            <div className="mt-1 flex items-center justify-between gap-2 border-border/60 border-l-2 pl-2 text-muted-foreground text-xs">
                              <span>ເງິນທອນ</span>
                              <span className="shrink-0 tabular-nums">
                                {change.toLocaleString("lo-LA")} ₭
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </OrderDetailSection>
            )}

            <OrderDetailActions>
              {order.status === "OPEN" && (
                <OrderDetailActionGroup title="Order">
                  <Button
                    className="h-11 w-full font-semibold"
                    onClick={handlePrintReceipt}
                    disabled={completeOrderMutation.isPending}
                  >
                    <DollarSign className="mr-2 h-4 w-4" />
                    ຈ່າຍເງິນ
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => setIsAddItemsDialogOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    ເພີ່ມລາຍການ
                  </Button>
                  {canUpdateTable && (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setIsTableDialogOpen(true)}
                      disabled={updateTableMutation.isPending}
                    >
                      <Table className="mr-2 h-4 w-4" />
                      {tableName ? "ຍ້າຍໂຕະ" : "ຜູກໂຕະ"}
                    </Button>
                  )}
                  {canMoveItems && (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setIsOrderDialogOpen(true)}
                      disabled={moveItemsMutation.isPending}
                    >
                      <Move className="mr-2 h-4 w-4" />
                      ຍ້າຍລາຍການ
                    </Button>
                  )}
                  {canVoid && (
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={() => setIsVoidDialogOpen(true)}
                      disabled={voidOrderMutation.isPending}
                    >
                      <X className="mr-2 h-4 w-4" />
                      ຍົກເລີກ Order
                    </Button>
                  )}
                </OrderDetailActionGroup>
              )}
              {canRefund && (
                <Button
                  className="w-full"
                  variant="destructive"
                  onClick={() => setIsRefundDialogOpen(true)}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  ຄືນເງິນ
                </Button>
              )}
              <OrderDetailActionGroup title="Print">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={handlePrintProformaOnly}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  ພິມ Proforma
                </Button>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={handlePrintKitchenOnly}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  ພິມ Kitchen
                </Button>
                {canReprint && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={handlePrintReceiptOnly}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    ພິມ Receipt
                  </Button>
                )}
              </OrderDetailActionGroup>
            </OrderDetailActions>
          </>
        }
        footer={
          refunds.length > 0 ? (
            <div className="mx-auto max-w-6xl px-4 pb-4">
              <RefundList
                refunds={refunds}
                branchId={branchId}
                onRefundUpdated={() => {
                  // Refetch will be handled by query invalidation
                }}
              />
            </div>
          ) : undefined
        }
      >
        <OrderDetailSection title="ຂໍ້ມູນ Order">
          <OrderDetailMetaGrid
            items={[
              {
                icon: ShoppingBag,
                label: "ປະເພດ",
                value: getOrderTypeLabel(order.orderType),
              },
              ...(tableName
                ? [{ icon: Table, label: "ໂຕະ", value: tableName }]
                : []),
              ...(salespersonName
                ? [{ icon: User, label: "ຄົນຂາຍ", value: salespersonName }]
                : []),
              ...(customerData
                ? [
                    {
                      icon: Phone,
                      label: "ລູກຄ້າ",
                      value: `${customerData.firstName} ${customerData.lastName}${
                        customerData.phone ? ` (${customerData.phone})` : ""
                      }`,
                    },
                  ]
                : []),
            ]}
          />

          {order.orderType === "DELIVERY" && delivery && (
            <div className="mt-4 rounded-lg border bg-muted/20 p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                ຂໍ້ມູນການຈັດສົ່ງ
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground">ຊື່ຜູ້ຮັບ:</span>
                  <span className="font-medium">{delivery.recipientName}</span>
                </div>
                {delivery.recipientPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{delivery.recipientPhone}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="font-medium">{delivery.addressLine1}</p>
                    {delivery.addressLine2 && (
                      <p className="text-muted-foreground">
                        {delivery.addressLine2}
                      </p>
                    )}
                    {(delivery.district ||
                      delivery.province ||
                      delivery.postalCode) && (
                      <p className="text-muted-foreground">
                        {[
                          delivery.district,
                          delivery.province,
                          delivery.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {delivery.countryCode && (
                      <p className="text-muted-foreground text-xs">
                        {delivery.countryCode}
                      </p>
                    )}
                  </div>
                </div>
                {delivery.instructions && (
                  <div className="mt-2 rounded-md bg-background p-2 text-xs">
                    <strong>ຄຳແນະນຳ:</strong> {delivery.instructions}
                  </div>
                )}
              </div>
            </div>
          )}
        </OrderDetailSection>

        <OrderDetailSection
          title="ລາຍການສິນຄ້າ"
          description={`${items.length} ລາຍການ`}
        >
          <OrderDetailItemsList isEmpty={items.length === 0}>
            {items.map((item) => {
              const unitPrice = Number(item.unitPrice);
              const lineSubtotal = Number(item.lineSubtotal);
              const isVoided = !!(item as { voidedAt?: string | Date | null })
                .voidedAt;
              const canVoidLine =
                order.status === "OPEN" && !isVoided && branchId && orderId;

              return (
                <OrderDetailItemRow
                  key={item.id}
                  muted={isVoided}
                  name={
                    <>
                      <span
                        className={cn(
                          isVoided && "text-muted-foreground line-through",
                        )}
                      >
                        {item.productName}
                      </span>
                      {isVoided && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                          ຍົກເລີກແລ້ວ
                        </span>
                      )}
                    </>
                  }
                  details={
                    <>
                      {item.sku && (
                        <p className="text-muted-foreground text-xs">
                          {item.sku}
                        </p>
                      )}
                      {item.variantOptions && item.variantOptions.length > 0 && (
                        <div className="space-y-0.5">
                          {item.variantOptions.map((opt) => (
                            <p
                              key={`${opt.optionName}-${opt.optionValue}`}
                              className="text-muted-foreground text-xs"
                            >
                              {opt.optionName}:{" "}
                              <strong>{opt.optionValue}</strong>
                            </p>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <p className="text-muted-foreground text-xs italic">
                          {item.note}
                        </p>
                      )}
                      {item.modifiers.length > 0 && (
                        <div className="space-y-0.5">
                          {item.modifiers.map((modifier) => (
                            <p
                              key={modifier.id}
                              className="text-muted-foreground text-xs"
                            >
                              +{" "}
                              {(modifier as { optionLabel?: string })
                                .optionLabel
                                ? `${modifier.label}: ${(modifier as { optionLabel?: string }).optionLabel}`
                                : modifier.label}
                              {modifier.priceDelta !== 0 && (
                                <span className="ml-1">
                                  ({modifier.priceDelta > 0 ? "+" : ""}
                                  {Number(modifier.priceDelta).toLocaleString(
                                    "lo-LA",
                                  )}{" "}
                                  ₭)
                                </span>
                              )}
                            </p>
                          ))}
                        </div>
                      )}
                      {Number(item.taxTotal) > 0 && (
                        <p className="text-muted-foreground text-xs">
                          ພາສີ: {Number(item.taxTotal).toLocaleString("lo-LA")} ₭
                        </p>
                      )}
                    </>
                  }
                  quantityLine={
                    item.quantity > 1
                      ? `${item.quantity} x ${unitPrice.toLocaleString("lo-LA")} ₭`
                      : `ຈຳນວນ: ${item.quantity}`
                  }
                  total={`${lineSubtotal.toLocaleString("lo-LA")} ₭`}
                  actions={
                    canVoidLine ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-destructive hover:bg-destructive/10"
                        disabled={voidOrderItemMutation.isPending}
                        onClick={() => {
                          setVoidLineItemId(item.id);
                          setVoidLineReason("");
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : undefined
                  }
                />
              );
            })}
          </OrderDetailItemsList>
        </OrderDetailSection>
      </OrderDetailLayout>

      {/* Dialogs */}
      <TableSelectorDialog
        open={isTableDialogOpen}
        onOpenChange={setIsTableDialogOpen}
        branchId={branchId}
        currentTableId={order.tableId}
        onSelect={handleTableSelect}
      />
      <OrderSelectorDialog
        open={isOrderDialogOpen}
        onOpenChange={setIsOrderDialogOpen}
        branchId={branchId}
        excludeOrderId={order.id}
        onSelect={handleOrderSelect}
      />

      {orderId && (
        <AddItemsDialog
          open={isAddItemsDialogOpen}
          onOpenChange={setIsAddItemsDialogOpen}
          orderId={orderId}
        />
      )}

      <PaymentDialog
        open={isPaymentDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            clearCustomerDisplayOrder();
            clearCustomerDisplayCustomer();
          }
          setIsPaymentDialogOpen(open);
        }}
        orderTotal={Number(order.grandTotal)}
        isSubmitting={completeOrderMutation.isPending}
        onComplete={(
          payments,
          voucherCodes,
          couponCode,
          couponOrderItemIds,
          couponApplications,
          paymentCustomerId,
          _customerSelection,
          rewardId,
        ) =>
          handleCompleteOrder(
            payments,
            voucherCodes,
            couponCode,
            couponOrderItemIds,
            couponApplications,
            paymentCustomerId,
            rewardId,
          )
        }
        branchId={branchId}
        orderId={orderId}
        customerId={order.customerId}
        initialCustomer={
          order.customerId && customerData
            ? {
                id: order.customerId,
                firstName: customerData.firstName,
                lastName: customerData.lastName,
                phone: customerData.phone,
              }
            : null
        }
        voucherSectionDefaultOpen={true}
        orderItems={items
          .filter((i) => !i.voidedAt)
          .map((i) => ({
            id: i.id,
            productName: i.productName,
            lineSubtotal: Number(i.lineSubtotal),
            taxTotal: Number(i.taxTotal ?? 0),
            variantId: i.variantId,
            quantity: Number(i.quantity),
          }))}
      />

      <VoidOrderDialog
        open={isVoidDialogOpen}
        onOpenChange={setIsVoidDialogOpen}
        orderNo={order.orderNo}
        orderStatus={order.status}
        onConfirm={handleVoidOrder}
      />

      <RefundDialog
        open={isRefundDialogOpen}
        onOpenChange={setIsRefundDialogOpen}
        orderId={order.id}
        branchId={branchId}
        orderTotal={Number(order.grandTotal)}
        paidTotal={Number(order.paidTotal)}
      />

      {/* Dialog ยกเลิกลายการ – ต้องใส่เหตุผล */}
      <Dialog
        open={voidLineItemId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setVoidLineItemId(null);
            setVoidLineReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>ຍົກເລີກລາຍການ</DialogTitle>
            <DialogDescription>
              {voidLineItemId
                ? `ກຳລັງຍົກເລີກ: ${items.find((i) => i.id === voidLineItemId)?.productName ?? ""}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label
              htmlFor="void-line-reason"
              className="block font-medium text-muted-foreground text-sm"
            >
              ເຫດຜົນ (ບັງຄັບ)
            </label>
            <Input
              id="void-line-reason"
              value={voidLineReason}
              onChange={(e) => setVoidLineReason(e.target.value)}
              placeholder="ປ້ອນເຫດຜົນການຍົກເລີກລາຍການ..."
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setVoidLineItemId(null);
                setVoidLineReason("");
              }}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              disabled={
                !voidLineReason.trim() || voidOrderItemMutation.isPending
              }
              onClick={async () => {
                if (
                  !voidLineReason.trim() ||
                  !voidLineItemId ||
                  !branchId ||
                  !orderId
                )
                  return;
                try {
                  await voidOrderItemMutation.mutateAsync({
                    orderId,
                    branchId,
                    orderItemId: voidLineItemId,
                    reason: voidLineReason.trim(),
                  });
                  toast.success("ຍົກເລີກລາຍການສຳເລັດ");
                  setVoidLineItemId(null);
                  setVoidLineReason("");
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "ເກີດຂໍ້ຜິດພາດ");
                }
              }}
            >
              ຍົກເລີກລາຍການ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
